const wildfireSpotlightData = {
    displayName: 'wildfires',
    // Manually update when the spotlight's content changes.
    publishedOn: 'June 2026',
    hero: {
        image: require('../../../assets/img/spotlights/fireFight.jpg'),
        title: 'Wildfire Risk and Mitigation in Larimer County',
        description:
            'Wildfire has become a defining challenge for Colorado, and nowhere more so than in Larimer County, ' +
            'home to the largest wildfire in state history. As fire seasons intensify and communities in the ' +
            'wildland-urban interface face growing risk, understanding the data behind wildfire is more critical ' +
            'than ever. This Spotlight brings together the datasets, tools, and community resources that help ' +
            'Larimer County residents, staff, and policymakers understand and reduce wildfire risk.',
        tagline: 'Understanding risk is the first step toward resilience.',
    },

    sections: {
        dataSources: {
            title: 'Explore Key Data Sources',
            description:
                'Discover datasets that track wildfire risk, mitigation efforts, and air quality across Larimer County and Colorado.',
        },
        dataProducts: {
            title: 'Explore Wildfire Products and Tools',
            description:
                'These interactive tools, maps, and reports showcase how communities are understanding and responding to wildfire risk.',
        },
        starterPrompts: {
            title: "Don't Know Where to Start? Ask Bili!",
            description:
                "Here are some ways to explore Larimer County's wildfire story:",
        },
    },

    whyItMatters: {
        title: 'Why Wildfire Matters in Larimer County',
        intro:
            'Larimer County sits at the intersection of Colorado\u2019s mountains and its most populated ' +
            'corridor. Ranked as the most hazardous county in Colorado for wildfires, it was home to ' +
            'the Cameron Peak Fire in 2020, the largest wildfire in state history. Understanding ' +
            'wildfire risk here is not abstract. It affects homes, health, water quality, air quality, ' +
            'and the future of mountain and Front Range communities.',
        keyStats: [
            {
                value: '208,663',
                unit: 'acres',
                label: 'burned by the Cameron Peak Fire (2020), the largest wildfire in Colorado history',
                source: {
                    name: 'Colorado Encyclopedia',
                    url: 'https://coloradoencyclopedia.org/article/cameron-peak-fire',
                },
            },
            {
                value: '6x',
                label: 'increase in wildfire frequency in Colorado since the 1990s (from ~12 to ~72 fires/year)',
                source: {
                    name: 'CPR News / CSU REDI',
                    url: 'https://www.cpr.org/2025/08/29/colorado-wildfires-bigger-burning-faster-data-shows/',
                },
            },
            {
                value: '$9.8M',
                label: 'federal grant awarded to Larimer County for the "Protecting the Gateway to the Rockies" wildfire defense program',
                source: {
                    name: 'Larimer County',
                    url: 'https://www.larimer.gov/spotlights/2025/11/25/larimer-county-awarded-98m-grant-protecting-gateway-rockies-wildfire-defense',
                },
            },
            {
                value: '8/10',
                label: 'of the largest wildfires in Colorado history have occurred since 2012',
                source: {
                    name: 'CSU REDI',
                    url: 'https://csuredi.org/redi_reports/battle-scars-trends-in-wildfire-size-and-impact-across-colorado/',
                },
            },
        ],
        narrative: [
            {
                heading: 'A County on the Fire Line',
                text:
                    'In 2020, the Cameron Peak Fire burned 208,663 acres through the Arapaho and Roosevelt ' +
                    'National Forests, becoming the largest wildfire in Colorado\u2019s recorded history. ' +
                    'The fire destroyed 469 structures and forced the evacuation of over 6,000 residents ' +
                    'from communities including Red Feather Lakes, Glen Haven, Estes Park, and areas along ' +
                    'the Poudre Canyon. Just weeks later, the East Troublesome Fire burned 193,812 acres in ' +
                    'neighboring Grand County and crossed into Rocky Mountain National Park. Together, these ' +
                    'fires underscored that wildfire is not a distant threat for Larimer County\u2014it is a ' +
                    'neighbor-level reality for communities in the wildland-urban interface.',
                sources: [
                    {
                        name: 'Colorado Encyclopedia \u2013 Cameron Peak Fire',
                        url: 'https://coloradoencyclopedia.org/article/cameron-peak-fire',
                    },
                    {
                        name: 'Colorado Encyclopedia \u2013 East Troublesome Fire',
                        url: 'https://coloradoencyclopedia.org/article/east-troublesome-fire',
                    },
                ],
            },
            {
                heading: 'A Changing Climate, A Growing Risk',
                text:
                    'Colorado\u2019s wildfire risk is accelerating. The state has experienced a sixfold ' +
                    'increase in wildfire frequency since the 1990s, and the average fire size has grown ' +
                    'from 1,100 acres to nearly 1,800 acres. Scientists connect this trend to a 25-year ' +
                    'megadrought across the Southwest, the driest the region has been in 1,200 years. In ' +
                    'Larimer County, a sudden-onset \u201Cflash drought\u201D in 2024 primed forests for the ' +
                    '9,700-acre Alexander Mountain Fire west of Loveland, demonstrating how rapidly conditions ' +
                    'can shift even after above-average snowfall earlier in the season.',
                sources: [
                    {
                        name: 'CSU REDI \u2013 Battle Scars Report',
                        url: 'https://csuredi.org/redi_reports/battle-scars-trends-in-wildfire-size-and-impact-across-colorado/',
                    },
                    {
                        name: 'Colorado Sun \u2013 Flash Drought',
                        url: 'https://coloradosun.com/2024/08/08/northern-colorado-flash-drought-front-range-wildfires/',
                    },
                ],
            },
            {
                heading: 'Mitigation and Community Action',
                text:
                    'Wildfire risk is growing, but so is the response. Larimer County was awarded a $9.8 million ' +
                    'federal grant for the \u201CProtecting the Gateway to the Rockies\u201D program, funding ' +
                    'defensible space treatments on up to 900 parcels, community education through 75+ events, ' +
                    '500 acres of landscape-scale fuel treatments, and strategic fuels work along 7 miles of key ' +
                    'roadways. The Poudre Fire Authority offers free Home Ignition Zone Assessments, and Community ' +
                    'Wildfire Protection Plans in the Estes Valley, Glen Haven, and Loveland provide localized ' +
                    'frameworks for reducing risk. At the state level, the CO-SWAP program coordinates strategic ' +
                    'wildfire action across Colorado.',
                sources: [
                    {
                        name: 'Larimer County \u2013 Gateway to the Rockies Grant',
                        url: 'https://www.larimer.gov/spotlights/2025/11/25/larimer-county-awarded-98m-grant-protecting-gateway-rockies-wildfire-defense',
                    },
                    {
                        name: 'CO-SWAP Program',
                        url: 'https://dnr.colorado.gov/divisions/forestry/co-strategic-wildfire-action-program',
                    },
                ],
            },
        ],
        learnMore: [
            {
                name: 'Larimer County Fire Recovery',
                url: 'https://www.larimer.gov/emergency/resiliencyrecovery/fire-resources',
                description:
                    'Resources for fire recovery and resilience in Larimer County',
            },
            {
                name: 'CO-SWAP Program',
                url: 'https://dnr.colorado.gov/divisions/forestry/co-strategic-wildfire-action-program',
                description:
                    'Colorado\u2019s strategic wildfire action program for community-level mitigation',
            },
            {
                name: 'CSFS Fort Collins \u2013 Wildfire Mitigation',
                url: 'https://csfs.colostate.edu/fort-collins/fc-wildfire-mitigation-education/',
                description:
                    'Colorado State Forest Service wildfire mitigation and education resources',
            },
            {
                name: 'Poudre Fire Authority \u2013 Preparing Your Home',
                url: 'https://www.poudre-fire.org/living-safe/preparing-your-home-for-wildfires',
                description:
                    'How to protect your home from wildfire in the Fort Collins area',
            },
            {
                name: 'CSU REDI \u2013 Battle Scars Report',
                url: 'https://csuredi.org/redi_reports/battle-scars-trends-in-wildfire-size-and-impact-across-colorado/',
                description:
                    'Trends in wildfire size and impact across Colorado from CSU\u2019s Regional Economic Development Institute',
            },
        ],
    },

    reliefResources: {
        title: 'Active Fire Relief and Donation Resources',
        intro:
            'Several wildfires are actively burning across Colorado. The communities affected need support ' +
            'now. Below are verified relief funds and donation links for each active fire.',
        attribution: {
            text: 'Resource list compiled with help from the',
            name: 'NoCo Foundation',
            url: 'https://nocofoundation.org/noco-disaster-recovery-fund/',
        },
        fires: [
            {
                name: 'Snyder Mesa Fire',
                region: 'La Plata County',
                links: [
                    {
                        label: 'Red Cross – Colorado Wildfires',
                        url: 'https://www.redcross.org/about-us/news-and-events/news/2026/red-crossers-respond-to-western-wildfires.html',
                    },
                    {
                        label: 'National Fallen Firefighters Foundation',
                        url: 'https://www.firehero.org/donate/',
                    },
                ],
            },
            {
                name: 'Willow Fire',
                region: 'Lake County',
                links: [
                    {
                        label: 'Lake County Community Fund',
                        url: 'https://www.lakecountycommunityfund.org/',
                    },
                ],
            },
            {
                name: 'Gold Mountain Fire',
                region: 'Ouray County',
                links: [
                    {
                        label: 'Ouray County Fire Fund (Telluride Foundation)',
                        url: 'https://telluridefoundation.org/ouray-county-fire-fund/',
                    },
                ],
            },
            {
                name: 'Aspen Acres Fire',
                region: 'Huerfano County',
                links: [
                    {
                        label: 'United Way of Southern Colorado – Wildfire Relief',
                        url: 'https://unitedwaysoco.org/emergencyinfo',
                    },
                ],
            },
        ],
    },

    tribute: {
        title: 'Honoring Those Who Protect Our Communities',
        paragraphs: [
            'Wildfire season demands extraordinary courage. Across Colorado, thousands of firefighters, ' +
                'first responders, and volunteers risk their lives to defend communities, protect natural ' +
                'resources, and support evacuated families. Their work is often dangerous, exhausting, and ' +
                'underrecognized.',
            'We honor the firefighters who have lost their lives in the line of duty and those who continue ' +
                'to serve on the front lines. Their sacrifice makes resilience possible.',
        ],
    },

    // TODO: Replace with real data source entries from Engine API when available.
    // These are populated from the Geo Data Sources spreadsheet (Larimer Fire Data Products tab).
    dataSources: [
        {
            id: 1,
            title: 'Wildfire Risk to Communities (Larimer County)',
            description:
                'Interactive maps, charts, and resources to help communities understand, explore, and reduce wildfire risk.',
            metadata: 'Source: USDA / USFS | Updated: 2024',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 2,
            title: 'Larimer County Wildfire Map and Climate Risk Report',
            description:
                'Property-level wildfire risk projections with a 30-year slider, historical fire mapping, and community protection analysis.',
            metadata: 'Source: First Street | Updated: April 2026',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 3,
            title: 'Colorado Wildfire Risk Viewer',
            description:
                'Interactive map with wildfire risk themes, landscape characteristics, historical occurrences, and the 2020 forest action plan.',
            metadata: 'Source: Colorado State Forest Service',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 4,
            title: 'Average Wildfire Hazard Potential',
            description:
                'Wildfire hazard potential scores (0\u20135) for areas across the US, from non-burnable to very high risk.',
            metadata: 'Source: USGS | Updated: May 2026',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 5,
            title: 'Larimer County Air Quality Map',
            description:
                'Real-time air quality data from monitoring stations across Larimer County.',
            metadata: 'Source: IQAir | Updates frequently',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 6,
            title: 'Colorado Wildfire and Smoke Map',
            description:
                'Interactive map displaying active wildfires and smoke conditions with per-fire details.',
            metadata: 'Source: Coloradoan | Updates hourly',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 7,
            title: 'Rocky Mountain Area Fire Restriction Dashboard',
            description:
                'Current fire restrictions by state and county across the Rocky Mountain region.',
            metadata: 'Source: Interagency | Updates frequently',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 8,
            title: 'Larimer County Fire Restrictions and Bans Map',
            description:
                'Current fire restrictions and bans across three elevation zones in Larimer County.',
            metadata:
                'Source: Larimer County Sheriff\u2019s Office | Updates frequently',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 9,
            title: 'CDPHE Air Quality Monitoring Sites',
            description:
                'Currently reported Air Quality Index from monitoring stations across Colorado.',
            metadata: 'Source: CDPHE | Updates frequently',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 10,
            title: 'Larimer County Community Wildfire Protection Plan',
            description:
                'Comprehensive wildfire preparedness plan with static maps and mitigation information for Larimer County.',
            metadata: 'Source: Larimer County | Format: PDF',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
    ],

    // TODO: Replace with real data product entries from Engine API when available.
    // These are populated from the Geo Data Sources spreadsheet (Larimer Fire Data Products tab).
    dataProducts: [
        {
            id: 1,
            title: 'Protecting Larimer County StoryMap',
            description:
                'Mitigation efforts from the Sheriff\u2019s Office including fire crews, planning, and examples of wildfire mitigation across Larimer County.',
            metadata: 'Source: Larimer County Sheriff\u2019s Office | 2025',
            featured: true,
            image: null, // TODO: Add StoryMap preview image
            primaryCTA: {
                text: 'View StoryMap',
                link: 'https://storymaps.arcgis.com/stories/87747954c481433296dfc0a5572882c7',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 2,
            title: 'Larimer County Wildfire Maps',
            description:
                'Collection of maps covering wildfire mitigation, planning, and hazardous areas specific to Larimer County.',
            metadata: 'Source: Larimer County',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Maps',
                link: 'https://www.larimer.gov/emergency/fires/maps',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 3,
            title: 'Larimer County Mitigation Experience',
            description:
                'Interactive maps showing mitigation efforts and a forest tracker that shows how fires are impacting Larimer County forests.',
            metadata: 'Source: Larimer County Sheriff\u2019s Office',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Experience',
                link: 'https://experience.arcgis.com/experience/4cdd565d89044505b9e41042222e4e7c/page/Mitigation',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 4,
            title: 'Firewise USA Sites in Good Standing',
            description:
                'Interactive map of all Firewise stations in the US with preparedness resources for homeowners.',
            metadata: 'Source: NFPA Firewise USA',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Map',
                link: 'https://www.nfpa.org/Education-and-Research/Wildfire/Firewise-USA',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 5,
            title: 'Larimer County CWPP',
            description:
                'Community Wildfire Protection Plan with maps and detailed information on county preparedness and fire response.',
            metadata: 'Source: Larimer County | Format: PDF',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Plan',
                link: 'https://www.larimer.gov/sites/default/files/larimer-cwppdraft-review.pdf',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
    ],

    starterPrompts: [
        'What is the wildfire risk in my area of Larimer County?',
        'What is defensible space and how do I create it around my home?',
        'What are the key wildfire statistics for Larimer County I can use in a presentation?',
        'How has wildfire activity changed in Colorado over the past 20 years?',
        'What wildfire mitigation programs are available in Larimer County?',
        'How do wildfires affect air quality in Fort Collins and the Front Range?',
        'What should I know about evacuation routes in the Poudre Canyon area?',
    ],
};

export default wildfireSpotlightData;
